/**
 * Sidebar (TASK-103a/b) — left navigation column inside the AppShell navbar.
 *
 * Composition (top → bottom per design §4.3):
 *  - Wordmark + version footer info (rendered inside the AppShell.Navbar
 *    scrollable area).
 *  - Role-aware navigation (delegated to `<SidebarNavigation>`).
 *  - Footer: just the version badge. The legacy "Поддержка" shortcut was
 *    removed 2026-05-24 (CEO call) — it duplicated «Обращения» in
 *    «Служебное» and misled users to expect a FAQ. When an actual admin
 *    FAQ / help-docs page ships, this slot is the natural place for it.
 */

import { ScrollArea, Stack, Text } from '@mantine/core';
import { useAuth } from '@/hooks/use-auth';
import { useModerationQueueCount } from '@/api/tracks';
import { isStaff } from '@/lib/staff-route';
import { SidebarNavigation } from './SidebarNavigation';
import classes from './Sidebar.module.css';

const APP_VERSION = '0.1.0';

export interface SidebarProps {
  /** Desktop-only icon-only mode. Mobile drawer ignores this — it always
   *  shows full labels because the overlay has plenty of room. */
  collapsed?: boolean;
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const { user } = useAuth();
  const staff = isStaff(user?.role);
  const moderationCount = useModerationQueueCount(staff);

  return (
    <Stack
      gap={0}
      className={classes.sidebar}
      h="100%"
      justify="space-between"
    >
      <ScrollArea className={classes.scroll} type="auto">
        <Stack gap="md" p={collapsed ? 4 : 'sm'}>
          <SidebarNavigation
            collapsed={collapsed}
            badges={{ moderation: moderationCount.data }}
          />
        </Stack>
      </ScrollArea>

      <div className={classes.footer}>
        <Text size="xs" c="dimmed" ta={collapsed ? 'center' : 'left'}>
          {collapsed ? `v${APP_VERSION}` : `musichead · v${APP_VERSION}`}
        </Text>
      </div>
    </Stack>
  );
}
